import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Download, Share2, MapPin } from "lucide-react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
} from "@mui/material";
import Login_bg from "../../assets/images/truck.png";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import MediaViewer from "../../components/MediaViewer";
import SlideViewModal from "../../components/SlideViewer";
import LocationMapModal from "../../components/LocationMapModal";
import { getErrorMessage } from "../../utils/error";
import {
  getScanStageReportDetails,
  getScanPdf,
  getScanImagesZip,
  getContainerLocationDetails,
} from "./api";
import { errorMessage } from "../../components/Toaster";

const ASSET_LABELS = {
  Container: {
    reportTitle: "Container Inspection Report",
    idLabel: "Container ID",
    capacityLabel: "Container Capacity",
    typeLabel: "Container Type",
    codeLabel: "ISO Code",
    numberLabel: "Container Number",
  },
  IsoTank: {
    reportTitle: "Tank Inspection Report",
    idLabel: "Tank ID",
    capacityLabel: "Tank Capacity",
    typeLabel: "Tank Type",
    codeLabel: "Tank Code",
    numberLabel: "Tank Number",
  },
  Other: {
    reportTitle: "Asset Inspection Report",
    idLabel: "Asset ID",
    capacityLabel: "Asset Capacity",
    typeLabel: "Asset Type",
    codeLabel: "ISO Code",
    numberLabel: "Container Number",
  },
};

const ReportDetails = () => {
  const { scanStageId } = useParams();
  const [searchParams] = useSearchParams();
  const isLocationOnly = searchParams.get("type") === "location";
  const location_map = new Map();

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [defects, setDefects] = useState([]);
  const [addressData, setAddressData] = useState("-");
  const [insignificant, setInsignificant] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [viewType, setViewType] = useState("images");
  const [imageType, setImageType] = useState("raw");
  const [compressedRawImages, setCompressedRawImages] = useState([]);
  const [compressedProcessedImages, setCompressedProcessedImages] = useState(
    [],
  );
  const [compressedHeatmapImages, setCompressedHeatmapImages] = useState([]);
  const [compressedSegmentedImages, setCompressedSegmentedImages] = useState(
    [],
  );
  const [compressedContainerNumberImages, setCompressedContainerNumberImages] =
    useState([]);
  const [compressedIsoNumberImages, setCompressedIsoNumberImages] = useState(
    [],
  );
  const [compressedPlateImages, setCompressedPlateImages] = useState([]);
  const [compressedSealCodeImages, setCompressedSealCodeImages] = useState([]);
  const [isThumbLoading, setIsThumbLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [slideViewOpen, setSlideViewOpen] = useState(false);
  const [currentDefects, setCurrentDefects] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);

  // Derive labels from reportData.asset_type once data is loaded
  const labels =
    ASSET_LABELS[reportData?.asset_type] || ASSET_LABELS["Container"];

  useEffect(() => {
    if (!scanStageId) return;
    if (isLocationOnly) {
      fetchLocationDetails();
    } else {
      fetchReportDetails();
    }
  }, [scanStageId, isLocationOnly]);

  useEffect(() => {
    if (reportData?.video) {
      setViewType("video");
    } else if (reportData?.scan_images && reportData.scan_images.length > 0) {
      setViewType("images");
    }
  }, [reportData]);

  useEffect(() => {
    const rawImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("original"),
    );
    const processedImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("annotated"),
    );
    const heatmapImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("heatmap"),
    );
    const segmentedImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("area"),
    );
    const containerNumberImages = reportData.scan_images?.filter((img) => {
      const fileName = img.file_name?.toLowerCase() || "";

      return (
        fileName.includes("container_number") ||
        fileName.includes("tank_number")
      );
    });
    const isoNumberImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("iso_number"),
    );
    const plateImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("plate"),
    );
    const sealCodeImages = reportData.scan_images?.filter((img) =>
      img.file_name?.toLowerCase().includes("seal_code"),
    );

    async function compressImages() {
      setIsThumbLoading(true);
      try {
        if (rawImages?.length) {
          const compressed = await Promise.all(
            rawImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedRawImages(compressed);
        }
        if (processedImages?.length) {
          const compressed = await Promise.all(
            processedImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedProcessedImages(compressed);
        }
        if (heatmapImages?.length) {
          const compressed = await Promise.all(
            heatmapImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedHeatmapImages(compressed);
        }
        if (segmentedImages?.length) {
          const compressed = await Promise.all(
            segmentedImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedSegmentedImages(compressed);
        }
        if (containerNumberImages?.length) {
          const compressed = await Promise.all(
            containerNumberImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedContainerNumberImages(compressed);
        }
        if (isoNumberImages?.length) {
          const compressed = await Promise.all(
            isoNumberImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedIsoNumberImages(compressed);
        }
        if (plateImages?.length) {
          const compressed = await Promise.all(
            plateImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedPlateImages(compressed);
        }
        if (sealCodeImages?.length) {
          const compressed = await Promise.all(
            sealCodeImages.map(async (img) => ({
              thumb: await compressPreviewImage(img.file_url),
              original: img.file_url,
              file_name: img.file_name,
            })),
          );
          setCompressedSealCodeImages(compressed);
        }
      } finally {
        setIsThumbLoading(false);
      }
    }

    if (
      rawImages?.length ||
      processedImages?.length ||
      heatmapImages?.length ||
      segmentedImages?.length ||
      containerNumberImages?.length ||
      isoNumberImages?.length ||
      plateImages?.length ||
      sealCodeImages?.length
    ) {
      compressImages();
    }
  }, [reportData.scan_images]);

  const fetchLocationDetails = async () => {
    if (!scanStageId) return;
    setIsLoading(true);
    try {
      const res = await getContainerLocationDetails(scanStageId);
      setReportData(res.data);
      if (res.data.latitude && res.data.longitude) {
        const geoRes = await reverseGeoSearch(
          res.data.latitude,
          res.data.longitude,
        );
        setAddressData(geoRes?.display_name || "-");
      }
    } catch (err) {
      console.error("Failed to load location data", err);
      const message = getErrorMessage(err);
      errorMessage(message || "Failed to load location data.");
    }
    setIsLoading(false);
  };

  const fetchReportDetails = async () => {
    if (!scanStageId) return;
    setIsLoading(true);
    try {
      const res = await getScanStageReportDetails(scanStageId);
      setReportData(res.data);
      setDefects(res.data.defects);
      if (res.data.latitude && res.data.longitude) {
        const geoRes = await reverseGeoSearch(
          res.data.latitude,
          res.data.longitude,
        );
        setAddressData(geoRes?.display_name || "-");
      }
    } catch (err) {
      console.error("Failed to load report data", err);
      const message = getErrorMessage(err);
      errorMessage(message || "Failed to load report data.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setCurrentDefects(
      defects
        .filter((defect) =>
          insignificant
            ? !defect.is_significant_defect
            : defect.is_significant_defect,
        )
        .map((defect) => ({ ...defect, isChecked: false })),
    );
  }, [defects, insignificant]);

  useEffect(() => {
    if (checkAll) {
      setCurrentDefects((prev) =>
        prev.map((defect) => ({ ...defect, isChecked: true })),
      );
    } else {
      setCurrentDefects((prev) =>
        prev.map((defect) => ({ ...defect, isChecked: false })),
      );
    }
  }, [checkAll]);

  const handleTabChange = (insignificant) => {
    setInsignificant(insignificant);
    setCheckAll(false);
  };

  const handleCheck = (defectId, checked) => {
    setCurrentDefects((prev) =>
      prev.map((defect) =>
        defect.defect_id === defectId
          ? { ...defect, isChecked: checked }
          : defect,
      ),
    );
  };

  const reverseGeoSearch = async (lat, lon) => {
    try {
      const key = `${lat},${lon}`;
      if (location_map.has(key)) return location_map.get(key);
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=0`,
      );
      const data = response.data;
      location_map.set(key, data);
      return data;
    } catch (error) {
      console.error("Error in reverseGeoSearch:", error);
      return null;
    }
  };

  const downloadReport = async () => {
    if (!reportData?.scan_id) return;
    setSnackbarOpen(true);
    try {
      await getScanPdf(reportData.scan_id, reportData.stage_name);
    } catch (error) {
      console.error("Failed to download report", error);
      const message = getErrorMessage(error);
      errorMessage(message || "Failed to download report.");
    }
    setSnackbarOpen(false);
  };

  const handleImageTypeTab = (type) => setImageType(type);

  const shareReport = async (reportData) => {
    setIsSharing(true);
    const containerNumber = reportData?.container_number || "N/A";
    const images = reportData?.scan_images || [];
    const videos = reportData?.video || [];
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const subject = `Survey Report For ${labels.numberLabel} ${containerNumber}`;
    let bodyText = `Hi,\n\nThe report for the ${labels.idLabel} ${containerNumber} is shared below.\n\n`;

    try {
      if (images.length > 0) {
        const zipRes = await getScanImagesZip(reportData.scan_stage_id);
        if (zipRes?.status && zipRes?.data) {
          bodyText += `Images:\nSurvey_Images_${containerNumber}.zip\n${zipRes.data}\n\n`;
        } else {
          bodyText += `Images archive could not be generated.\n\n`;
        }
      } else if (videos.length > 0) {
        const video = videos[0];
        bodyText += `Video file:\n${video.file_name}\n${video.file_url}\n\n`;
      } else {
        bodyText += `No files available for this ${labels.idLabel}.\n\n`;
      }
    } catch (err) {
      console.error("Failed to share report:", err);
      bodyText += `Report files could not be generated.\n\n`;
    }

    bodyText += `Note: The shared download link will remain active for one month from ${formattedDate}.\n\nThanks`;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      if (navigator.share) {
        try {
          await navigator.share({ title: subject, text: bodyText });
        } catch (err) {
          if (err.name !== "AbortError") {
            window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
          }
        }
      } else {
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
      }
    } else {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`,
        "_blank",
      );
    }
    setIsSharing(false);
  };

  function compressPreviewImage(fileUrl, quality = 0.2, maxWidth = 100) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const scale = maxWidth / img.width;
        const canvas = document.createElement("canvas");
        canvas.width = maxWidth;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => {
        console.warn("Image failed to load:", fileUrl);
        resolve(Login_bg);
      };
      img.src = fileUrl;
    });
  }

  const getCurrentImages = () => {
    switch (imageType) {
      case "raw":
        return compressedRawImages;
      case "processed":
        return compressedProcessedImages;
      case "heatmap":
        return compressedHeatmapImages;
      case "segmented":
        return compressedSegmentedImages;
      case "container_number":
      case "tank_number":
        return compressedContainerNumberImages;
      case "iso_number":
        return compressedIsoNumberImages;
      case "plate":
        return compressedPlateImages;
      case "seal_code":
        return compressedSealCodeImages;
      default:
        return [];
    }
  };

  const imageTabs = [
    { key: "raw", label: "Raw" },
    { key: "processed", label: "Processed" },
    ...(compressedHeatmapImages.length > 0
      ? [{ key: "heatmap", label: "Heatmap" }]
      : []),
    ...(compressedSegmentedImages.length > 0
      ? [{ key: "segmented", label: "Segmented" }]
      : []),
    ...(compressedContainerNumberImages.length > 0
      ? [
          {
            key:
              reportData?.asset_type === "IsoTank"
                ? "tank_number"
                : "container_number",
            label: labels.numberLabel,
          },
        ]
      : []),
    ...(compressedIsoNumberImages.length > 0
      ? [{ key: "iso_number", label: "ISO No." }]
      : []),
    ...(compressedPlateImages.length > 0
      ? [{ key: "plate", label: "Plate" }]
      : []),
    ...(compressedSealCodeImages.length > 0
      ? [{ key: "seal_code", label: "Seal Code" }]
      : []),
  ];

  const hasLocation = reportData?.latitude && reportData?.longitude;

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-screen w-full">
          <Loader color="primary" size={40} />
        </div>
      ) : (
        <div className="flex w-full min-h-screen overflow-y-auto bg-gray-200">
          <div className="flex-1 p-2 sm:p-4 md:p-8">
            {!isLocationOnly && (
              <div className="p-3 sm:p-6 bg-white rounded-lg shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <h1 className="text-base sm:text-xl font-semibold">
                    {labels.reportTitle}
                  </h1>
                </div>
                <div className="border rounded p-3 sm:p-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-4 text-sm">
                    {/* Col 1 */}
                    <div className="space-y-3 pb-4 mb-4 border-b md:border-b-0 md:mb-0 md:pb-0">
                      <div>
                        <span className="font-medium">Report Number : </span>
                        <span>{reportData.report_number || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">{labels.idLabel} : </span>
                        <span>{reportData.container_number}</span>
                      </div>
                      <div>
                        <span className="font-medium">Survey Date : </span>
                        <span>
                          {reportData.created_at
                            ? new Date(
                                reportData.created_at,
                              ).toLocaleDateString("en-GB")
                            : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Carrier type : </span>
                        <span>
                          {reportData.carrier_type?.carriertype || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Captured by : </span>
                        <span>
                          {reportData?.captured_by_first_name}
                          {reportData?.captured_by_last_name
                            ? ` ${reportData.captured_by_last_name}`
                            : ""}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Company : </span>
                        <span>{reportData.company_name || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Comment : </span>
                        <span>{reportData?.comment || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Location : </span>
                        <span className="break-words">{addressData}</span>
                      </div>
                    </div>

                    {/* Col 2 */}
                    <div className="space-y-3 pb-4 mb-4 border-b md:border-b-0 md:mb-0 md:pb-0">
                      <div>
                        <span className="font-medium">Scan ID : </span>
                        <span className="break-all">
                          {reportData.scan_id || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">
                          {labels.capacityLabel} :{" "}
                        </span>
                        <span>{reportData.container_capacity || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Survey Time : </span>
                        <span>
                          {reportData.created_at
                            ? new Date(
                                reportData.created_at,
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Manager : </span>
                        <span>
                          {reportData?.manager_first_name}
                          {reportData?.manager_last_name &&
                          reportData.manager_last_name !== "null"
                            ? ` ${reportData.manager_last_name}`
                            : ""}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Load Status : </span>
                        <span>{reportData.load_status?.loadstatus || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Yard : </span>
                        <span>{reportData.yard_name || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">
                          {labels.codeLabel} :{" "}
                        </span>
                        <span>{reportData?.iso_code || "-"}</span>
                      </div>
                    </div>

                    {/* Col 3 */}
                    <div className="space-y-3">
                      <div>
                        <span className="font-medium">Stage : </span>
                        <span>{reportData.stage_name || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">
                          {labels.typeLabel} :{" "}
                        </span>
                        <span>
                          {reportData.container_type?.containertype || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Entry Date : </span>
                        <span>
                          {reportData.entry_time ? reportData.entry_time : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Source : </span>
                        <span>{reportData.camera_name || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Contact : </span>
                        <span>{reportData.captured_contact || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Shipping Line : </span>
                        <span>{reportData.shipping_line_name || "-"}</span>
                      </div>
                      <div>
                        <span className="font-medium">Status : </span>
                        <span>
                          {reportData.report_published
                            ? "Approved"
                            : reportData.reported_to_manager
                              ? "To Be Approved"
                              : isLocationOnly
                                ? "Location Only"
                                : "To Be Reviewed"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Media Card ── */}
            {!isLocationOnly && (
              <div className="p-3 sm:p-6 bg-white rounded-lg shadow-md my-5">
                <FormControl component="fieldset" className="mb-4 w-full">
                  <div className="flex items-center w-full">
                    <RadioGroup
                      row
                      value={viewType}
                      onChange={(e) => setViewType(e.target.value)}
                    >
                      <FormControlLabel
                        value="images"
                        control={<Radio />}
                        label="Images"
                      />
                      <FormControlLabel
                        value="video"
                        control={<Radio />}
                        label="Video"
                      />
                    </RadioGroup>
                    {viewType === "images" && (
                      <button
                        title="View images slideshow"
                        onClick={() => setSlideViewOpen(true)}
                        className="ml-auto w-8 h-8 flex items-center justify-center rounded border border-blue-600 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="9"
                            y="1"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="1"
                            y="9"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                          <rect
                            x="9"
                            y="9"
                            width="6"
                            height="6"
                            rx="1.2"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  {viewType === "images" && (
                    <div className="flex gap-0 mt-2 overflow-x-auto border-b">
                      {imageTabs.map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => handleImageTypeTab(tab.key)}
                          className={`focus:outline-none py-3 px-3 text-xs sm:text-sm whitespace-nowrap flex-shrink-0 font-medium transition-colors ${
                            imageType === tab.key
                              ? "border-b-2 border-blue-600 text-blue-600"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  )}
                </FormControl>
                {viewType === "images" && isThumbLoading ? (
                  <div className="flex justify-center items-center h-20">
                    <Loader size={40} />
                  </div>
                ) : (
                  <MediaViewer
                    images={getCurrentImages()}
                    video={reportData.video}
                    viewType={viewType}
                    imageType={imageType}
                    existingDefects={defects}
                    rawImages={compressedRawImages}
                  />
                )}
                <SlideViewModal
                  open={slideViewOpen}
                  onClose={() => setSlideViewOpen(false)}
                  allImages={reportData.scan_images || []}
                />
              </div>
            )}

            {/* ── Defects Card ── */}
            {!isLocationOnly ? (
              <div className="p-3 sm:p-6 bg-white rounded-lg shadow-md my-5">
                <div className="border-b mb-6">
                  <div className="flex gap-0 overflow-x-auto">
                    <button
                      onClick={() => handleTabChange(false)}
                      className={`focus:outline-none py-3 px-4 text-sm whitespace-nowrap font-medium transition-colors ${
                        insignificant === false
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Defect to be considered
                    </button>
                    <button
                      onClick={() => handleTabChange(true)}
                      className={`focus:outline-none py-3 px-4 text-sm whitespace-nowrap font-medium transition-colors ${
                        insignificant === true
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Insignificant defects
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    <Button
                      onClick={downloadReport}
                      label="DOWNLOAD REPORT"
                      icon={<Download size={15} />}
                      iconPosition="end"
                      variant="outlined"
                      className="text-xs sm:text-sm w-full sm:w-auto"
                    />
                    {Boolean(hasLocation) && (
                      <Button
                        onClick={() => setMapOpen(true)}
                        label="Location"
                        icon={<MapPin size={15} />}
                        iconPosition="end"
                        variant="outlined"
                        color="primary"
                        className="text-xs sm:text-sm w-full sm:w-auto"
                      />
                    )}
                    <Button
                      disabled
                      label="PUBLISH THE REPORT"
                      variant="outlined"
                      color="action"
                      className="text-xs sm:text-sm w-full sm:w-auto"
                    />
                    <Button
                      label={`MOVE TO ${insignificant ? "SIGNIFICANT" : "INSIGNIFICANT"} DEFECT`}
                      icon={<ArrowRight size={15} />}
                      iconPosition="end"
                      disabled
                      variant="outlined"
                      color="primary"
                      className="text-xs sm:text-sm w-full sm:w-auto"
                    />
                    <Button
                      onClick={() => shareReport(reportData)}
                      label={isSharing ? "Sharing..." : "Share Report"}
                      icon={
                        isSharing ? (
                          <svg
                            className="animate-spin h-4 w-4"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <Share2 size={15} />
                        )
                      }
                      iconPosition="end"
                      variant="outlined"
                      disabled={isSharing}
                      className="text-xs sm:text-sm w-full sm:w-auto"
                    />
                  </div>

                  <div className="border rounded-lg overflow-x-auto">
                    <table className="min-w-full text-xs sm:text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="w-10 px-2 sm:px-4 py-2 sm:py-3">
                            <input
                              onChange={(e) =>
                                setCheckAll(e.currentTarget.checked)
                              }
                              checked={checkAll}
                              type="checkbox"
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap">
                            Defect ID
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap">
                            Defect Type
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap">
                            Defect Area (cm²)
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap">
                            Defect Side
                          </th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium whitespace-nowrap">
                            Image Number
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentDefects.map((defect, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              <input
                                type="checkbox"
                                onChange={(e) =>
                                  handleCheck(
                                    defect.defect_id,
                                    e.currentTarget.checked,
                                  )
                                }
                                checked={defect.isChecked}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              {defect.defect_no}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              {defect.defect_name}
                            </td>
                            <td className="px-4 py-3">
                              {defect.defect_dimension?.area_cm2 > 0
                                ? defect.defect_dimension.area_cm2
                                : "-"}
                            </td>
                            <td className="px-4 py-3">
                              {defect.defect_side &&
                              defect.defect_side !== "UNKNOWN"
                                ? defect.defect_side
                                : "-"}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              {defect.image_no}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 sm:p-6 bg-white rounded-lg shadow-md my-5">
                <p className="text-sm text-gray-500 italic mb-4">
                  No survey has been conducted for this container yet. Only
                  location data is available.
                </p>
                {hasLocation && (
                  <Button
                    onClick={() => setMapOpen(true)}
                    label="Location"
                    icon={<MapPin size={15} />}
                    iconPosition="end"
                    variant="outlined"
                    color="primary"
                    className="text-xs sm:text-sm"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <LocationMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        latitude={reportData.latitude}
        longitude={reportData.longitude}
        address={reportData.address || addressData}
        block={reportData.block}
        bay={reportData.bay}
        row={reportData.row}
        tier={reportData.tier}
        accuracy={reportData.accuracy_meters}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={"Downloading..."}
        key="topright"
      />
    </>
  );
};

export default ReportDetails;
